import { CardShell } from '@atlas/ui-components'
import { Input, Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { PromptBodyEditor } from '@/components/prompts/PromptBodyEditor'
import { PromptBodyPreview } from '@/components/prompts/PromptBodyPreview'
import { PromptCompositionMap } from '@/components/prompts/PromptCompositionMap'
import { PromptPreviewForm } from '@/components/prompts/PromptPreviewForm'
import { PromptSettingsPanel } from '@/components/prompts/PromptSettingsPanel'
import { PromptVariablePanel } from '@/components/prompts/PromptVariablePanel'
import type { Prompt, PromptListRecord } from '@/domain/prompt.types'
import type { useGenerateOutputSchema, useRenderPrompt } from '@/hooks/usePrompts'
import type { usePromptEditor } from './usePromptEditor'

/**
 * The template editor beside its variable panel and preview.
 *
 * Split from the page so the page stays a shell (header, chips, tab wiring) and this
 * owns the authoring surface.
 */
export function EditorTab({
	prompt,
	editor,
	lists,
	render,
	suggestSchema,
}: {
	prompt: Prompt
	editor: ReturnType<typeof usePromptEditor>
	lists: PromptListRecord[]
	render: ReturnType<typeof useRenderPrompt>
	suggestSchema: ReturnType<typeof useGenerateOutputSchema>
}) {
	return (
		<div className="grid gap-4 lg:grid-cols-2">
			<CardShell as="section" aria-label="Template" className="p-4">
				<h3 className="mb-3 text-ds-sm font-medium text-ds-text">Template</h3>

				{editor.editing ? (
					<div className="space-y-3">
						<label className="block">
							<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Name</span>
							<Input
								value={editor.form.name}
								aria-label="Prompt name"
								onChange={(e) => editor.setForm({ ...editor.form, name: e.target.value })}
							/>
						</label>
						<label className="block">
							<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Description</span>
							<Input
								value={editor.form.description}
								aria-label="Prompt description"
								onChange={(e) => editor.setForm({ ...editor.form, description: e.target.value })}
							/>
						</label>
						<label className="block">
							<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
								Body — use {'{{variable}}'} placeholders
							</span>
							<PromptBodyEditor
								body={editor.form.body}
								variables={editor.form.variables}
								onChange={(body) => editor.setForm({ ...editor.form, body })}
							/>
						</label>
					</div>
				) : (
					<PromptBodyPreview body={prompt.body} variables={prompt.variables} />
				)}

				{editor.lint.undeclared.length > 0 ? (
					<p role="alert" className="mt-3 text-ds-sm text-ds-danger">
						Undeclared: {editor.lint.undeclared.map((k) => `{{${k}}}`).join(', ')} — add a variable or remove the
						placeholder before saving.
					</p>
				) : null}
				{editor.lint.unused.length > 0 ? (
					<p className="mt-2 text-ds-sm text-ds-warning">
						Declared but unused: {editor.lint.unused.join(', ')} — these are collected from callers and then ignored.
					</p>
				) : null}

				<PromptCompositionMap
					body={editor.editing ? editor.form.body : prompt.body}
					variables={editor.form.variables}
				/>
			</CardShell>

			<EditorPanels prompt={prompt} editor={editor} lists={lists} render={render} suggestSchema={suggestSchema} />
		</div>
	)
}

/**
 * The editor's right column: the variable schema, a live preview, and the model
 * config.
 *
 * Split from `EditorTab` because the two halves answer different questions — what
 * the template SAYS versus what its variables are and how it renders — and together
 * they exceeded the size ceiling.
 */
function EditorPanels({
	prompt,
	editor,
	lists,
	render,
	suggestSchema,
}: {
	prompt: Prompt
	editor: ReturnType<typeof usePromptEditor>
	lists: PromptListRecord[]
	render: ReturnType<typeof useRenderPrompt>
	suggestSchema: ReturnType<typeof useGenerateOutputSchema>
}) {
	// Tabs rather than three stacked panels: the preview needs the column's full
	// height to show composed text, and stacking pushed the model config below the
	// fold on any prompt with more than a couple of variables.
	return (
		<Tabs defaultSelectedKey="variables">
			<TabList variant="underline" className="mb-3" aria-label="Editor panels">
				<Tab id="variables">Variables</Tab>
				<Tab id="preview">Preview</Tab>
				<Tab id="model">Model config</Tab>
			</TabList>

			<TabPanel id="variables">
				<PromptVariablePanel
					variables={editor.form.variables}
					lists={lists}
					promptType={prompt.type}
					isDisabled={!editor.editing}
					undeclared={editor.lint.undeclared}
					unused={editor.lint.unused}
					onChange={(variables) => editor.setForm({ ...editor.form, variables })}
				/>
			</TabPanel>

			<TabPanel id="preview">
				<PromptPreviewForm
					// The DRAFT's variables, matching the `head: true` render below. Built from
					// the saved prompt they disagreed: change which parts a join composes, open
					// Preview, and the form still asked for the old set while the render used
					// the new one — with nothing on screen saying so.
					variables={editor.form.variables}
					lists={lists}
					// Already reported as dead by the panel beside this one, so the form marks
					// them rather than quietly discarding what is typed into them.
					unusedKeys={editor.lint.unused}
					isSubmitting={render.isPending}
					rendered={render.data?.renderedText}
					warnings={render.data?.warnings ?? []}
					// The server names the offending variable ("Unknown variable(s) in payload:
					// scene", "missing required variable"), and that is the whole diagnosis —
					// a generic string left an author to guess which of eight fields was wrong.
					error={
						render.isError
							? extractApiErrorMessage(render.error, 'The prompt could not be rendered with these values.')
							: undefined
					}
					// `head: true` previews the working draft against LIVE lists, which is what
					// an author is editing — not the frozen published snapshot.
					onSubmit={(values) => render.mutate({ id: prompt.id, values, head: true })}
				/>
			</TabPanel>

			<TabPanel id="model">
				<PromptSettingsPanel
					prompt={prompt}
					onSuggestSchema={() => suggestSchema.mutate(prompt.id)}
					isSuggesting={suggestSchema.isPending}
					suggestRequested={suggestSchema.isSuccess}
					onSave={editor.saveSettings}
					isSaving={editor.isSaving}
				/>
			</TabPanel>
		</Tabs>
	)
}
